import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/solid'
import {
  fetchExamples,
  postMatch,
  classifyDemoError,
  type MatchResponse,
  type DemoError,
  type HeroPair,
} from '../api'
import { SPORTS } from '../content/sports'
import { usePrefersReducedMotion } from '../lib/hooks'
import { RegisterModal } from '@components/registration/RegisterModal'

type Verdict = 'match' | 'no-match' | 'ambiguous'

function verdictFor(res: MatchResponse): Verdict {
  const d = res.details[0]
  if (!d) return 'no-match'
  if (d.ambiguous) return 'ambiguous'
  return d.match ? 'match' : 'no-match'
}

// Verdict colours are DATA semantics (not brand): green MATCH, red NO MATCH, amber AMBIGUOUS.
const VERDICT_META: Record<
  Verdict,
  { label: string; color: string; Icon: typeof CheckCircleIcon }
> = {
  match: { label: 'MATCH', color: '#22C55E', Icon: CheckCircleIcon },
  'no-match': { label: 'NO MATCH', color: '#EF4444', Icon: XCircleIcon },
  ambiguous: { label: 'AMBIGUOUS', color: '#F59E0B', Icon: QuestionMarkCircleIcon },
}

function ResultPanel({ result }: { result: MatchResponse }) {
  const detail = result.details[0]
  const verdict = verdictFor(result)
  const { label, color, Icon } = VERDICT_META[verdict]
  const pct = Math.round((detail?.confidence ?? 0) * 100)

  return (
    <div className="animate-slide-up rounded-xl border border-dash-border bg-dash-bg p-5">
      <div className="flex items-center gap-3">
        <Icon className="h-8 w-8 shrink-0" style={{ color }} aria-hidden="true" />
        <span className="text-2xl font-bold tracking-tight" style={{ color }}>
          {label}
        </span>
        <span className="ml-auto font-mono text-sm tabular-nums text-dash-secondary">
          {result.processing_time_ms.toFixed(2)} ms
        </span>
      </div>

      {/* Confidence bar */}
      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between text-xs text-dash-secondary">
          <span>Confidence</span>
          <span className="font-mono tabular-nums" style={{ color }}>
            {pct}%
          </span>
        </div>
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-dash-border"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Match confidence"
        >
          <div
            className="h-full rounded-full transition-[width] duration-500 ease-out"
            style={{ width: `${pct}%`, backgroundColor: color }}
          />
        </div>
      </div>

      {/* Method / reason chips */}
      {detail && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="rounded-md border border-dash-border bg-dash-surface px-2 py-1 font-mono text-xs text-dash-secondary">
            method: {detail.method}
          </span>
          {detail.reason_code && (
            <span className="rounded-md border border-dash-border bg-dash-surface px-2 py-1 font-mono text-xs text-dash-secondary">
              {detail.reason_code}
            </span>
          )}
          {detail.cached && (
            <span className="rounded-md border border-dash-accent/40 bg-dash-accent/10 px-2 py-1 font-mono text-xs text-dash-accent">
              cached
            </span>
          )}
        </div>
      )}

      {/* Normalized names */}
      {detail?.normalized_name && (
        <p className="mt-4 font-mono text-xs text-dash-secondary">
          <span className="text-dash-muted">normalized:</span> {result.reference}
          <span className="mx-1.5 text-dash-muted">&harr;</span>
          {detail.normalized_name}
        </p>
      )}
    </div>
  )
}

function ResultSkeleton() {
  return (
    <div className="rounded-xl border border-dash-border bg-dash-bg p-5">
      <div className="animate-pulse space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-dash-surface" />
          <div className="h-6 w-32 rounded bg-dash-surface" />
        </div>
        <div className="h-2 w-full rounded-full bg-dash-surface" />
        <div className="flex gap-2">
          <div className="h-6 w-24 rounded bg-dash-surface" />
          <div className="h-6 w-28 rounded bg-dash-surface" />
        </div>
      </div>
    </div>
  )
}

function ErrorNote({
  error,
  onGetKey,
}: {
  error: DemoError
  onGetKey: () => void
}) {
  if (error.kind === 'network') {
    return (
      <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-4 text-sm text-[#FCD34D]">
        Couldn&apos;t reach the demo service. Check your connection and try again.
      </div>
    )
  }
  if (error.kind === 'server') {
    return (
      <div className="rounded-xl border border-[#EF4444]/30 bg-[#EF4444]/10 p-4 text-sm text-[#FCA5A5]">
        Something went wrong running that match. Try again in a moment.
      </div>
    )
  }
  // rate_limited (also covers 401 during the open-demo transition)
  return (
    <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-4">
      <p className="text-sm text-[#FCD34D]">
        Demo limit reached
        {error.retryAfterSeconds ? ` — try again in ${error.retryAfterSeconds}s.` : '.'} Get a free
        API key for unlimited testing.
      </p>
      <button
        type="button"
        onClick={onGetKey}
        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-dash-accent to-dash-accent-2 px-3 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent"
      >
        Get a free API key
        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )
}

export function LiveDemo() {
  const [sport, setSport] = useState('soccer')
  const [nameA, setNameA] = useState('')
  const [nameB, setNameB] = useState('')
  const [result, setResult] = useState<MatchResponse | null>(null)
  const [error, setError] = useState<DemoError | null>(null)
  const [showSkeleton, setShowSkeleton] = useState(false)
  const [showRegister, setShowRegister] = useState(false)
  const [chipOffset, setChipOffset] = useState(0)
  const hoveringChips = useRef(false)
  const reducedMotion = usePrefersReducedMotion()

  const { data: examples } = useQuery({ queryKey: ['examples'], queryFn: fetchExamples })
  const heroPairs = examples?.hero_pairs ?? []

  const mutation = useMutation({
    mutationFn: postMatch,
    onMutate: () => {
      setError(null)
      setResult(null)
    },
    onSuccess: (data) => setResult(data),
    onError: (err) => setError(classifyDemoError(err)),
  })

  // Show a skeleton only if the request takes longer than 300ms.
  useEffect(() => {
    if (!mutation.isPending) {
      setShowSkeleton(false)
      return
    }
    const t = setTimeout(() => setShowSkeleton(true), 300)
    return () => clearTimeout(t)
  }, [mutation.isPending])

  // Rotate the 4 visible suggestion chips every 8s (paused on hover / reduced motion).
  useEffect(() => {
    if (reducedMotion || heroPairs.length <= 4) return
    const id = setInterval(() => {
      if (!hoveringChips.current) setChipOffset((o) => o + 4)
    }, 8000)
    return () => clearInterval(id)
  }, [reducedMotion, heroPairs.length])

  const visibleChips = useMemo<HeroPair[]>(() => {
    if (heroPairs.length === 0) return []
    const out: HeroPair[] = []
    const count = Math.min(4, heroPairs.length)
    for (let i = 0; i < count; i++) {
      out.push(heroPairs[(chipOffset + i) % heroPairs.length])
    }
    return out
  }, [heroPairs, chipOffset])

  function runMatch(s: string, a: string, b: string) {
    const ref = a.trim()
    const other = b.trim()
    if (!ref || !other) return
    mutation.mutate({ reference: ref, names: [other], sport: s })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    runMatch(sport, nameA, nameB)
  }

  function applyChip(pair: HeroPair) {
    setSport(pair.sport)
    setNameA(pair.nameA)
    setNameB(pair.nameB)
    runMatch(pair.sport, pair.nameA, pair.nameB)
  }

  const canSubmit = nameA.trim().length > 0 && nameB.trim().length > 0 && !mutation.isPending

  return (
    <div className="w-full rounded-2xl border border-dash-border bg-dash-surface p-5 shadow-xl shadow-black/40 sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22C55E] opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22C55E]" />
        </span>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-dash-secondary">
          Live demo &mdash; try a real match
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor="demo-sport" className="mb-1 block text-xs font-medium text-dash-secondary">
            Sport
          </label>
          <select
            id="demo-sport"
            value={sport}
            onChange={(e) => setSport(e.target.value)}
            className="w-full text-sm"
          >
            {SPORTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="demo-name-a" className="mb-1 block text-xs font-medium text-dash-secondary">
              Name A (reference)
            </label>
            <input
              id="demo-name-a"
              type="text"
              value={nameA}
              onChange={(e) => setNameA(e.target.value)}
              placeholder="Manchester United"
              autoComplete="off"
              className="w-full font-mono text-sm"
            />
          </div>
          <div>
            <label htmlFor="demo-name-b" className="mb-1 block text-xs font-medium text-dash-secondary">
              Name B
            </label>
            <input
              id="demo-name-b"
              type="text"
              value={nameB}
              onChange={(e) => setNameB(e.target.value)}
              placeholder="Man Utd"
              autoComplete="off"
              className="w-full font-mono text-sm"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-dash-accent px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-dash-accent/90 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent focus-visible:ring-offset-2 focus-visible:ring-offset-dash-surface"
        >
          {mutation.isPending && (
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          )}
          {mutation.isPending ? 'Matching…' : 'Match'}
        </button>
      </form>

      {/* Suggestion chips */}
      {visibleChips.length > 0 && (
        <div
          className="mt-4"
          onMouseEnter={() => (hoveringChips.current = true)}
          onMouseLeave={() => (hoveringChips.current = false)}
        >
          <p className="mb-2 text-xs text-dash-muted">Try one of these:</p>
          <div className="flex flex-wrap gap-2">
            {visibleChips.map((pair, i) => (
              <button
                key={`${pair.sport}-${pair.nameA}-${pair.nameB}-${i}`}
                type="button"
                onClick={() => applyChip(pair)}
                className="rounded-full border border-dash-border bg-dash-bg px-3 py-1.5 font-mono text-xs text-dash-secondary transition-colors hover:border-dash-accent/60 hover:text-dash-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent"
              >
                {pair.nameA} <span className="text-dash-muted">&harr;</span> {pair.nameB}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Result / states */}
      <div className="mt-5">
        {error ? (
          <ErrorNote error={error} onGetKey={() => setShowRegister(true)} />
        ) : mutation.isPending && showSkeleton ? (
          <ResultSkeleton />
        ) : result ? (
          <ResultPanel result={result} />
        ) : null}
      </div>

      {showRegister && <RegisterModal onClose={() => setShowRegister(false)} redirectTo="/admin" />}
    </div>
  )
}
